import { type ErrorComponentProps, SearchParamError } from '@tanstack/react-router'
import { TRPCClientError } from '@trpc/client'
import { memo } from 'react'

import { ensureError } from '@orus.eu/error'
import { FunctionnalProblemError, InvalidLinkError, NotFoundError, TemporaryProblemError } from '@orus.eu/pharaoh'

import { logger } from '../../lib/logger.ts'
import { TrpcErrorHandler } from '../../lib/trpc-errorhandler.tsx'
import { CrashPage } from '../organisms/crash-page.tsx'
import { GenericProblemMessage } from '../organisms/generic-problem-message.tsx'
import { InvalidLinkMessage } from '../organisms/invalid-link-message.tsx'
import { NotFound } from '../organisms/not-found.tsx'
import { TemporaryFailureScreen } from '../organisms/temporary-failure-screen.tsx'

import { ChatWrapper } from './chat-wrapper.tsx'

export const RootErrorBoundary = memo(function RootErrorBoundary(props: ErrorComponentProps) {
  return (
    // The `ChatWrapper` is already added to the root route, but if the root route itself crashes (e.g. wrong search parameters)
    // it's not wrapped. Therefore, we need to wrap it here to have access to the chat
    // Note that the `ChatWrapper` is made to support multiple instances in the tree, so this is safe
    <ChatWrapper>
      <ErrorBoundary {...props} />
    </ChatWrapper>
  )
})

const ErrorBoundary = memo(function ErrorBoundary(props: ErrorComponentProps) {
  const error = ensureError(props.error)

  if (error instanceof TRPCClientError) {
    return <TrpcErrorHandler err={error} />
  }

  if (error instanceof TemporaryProblemError) {
    return <TemporaryFailureScreen />
  }

  if (error instanceof FunctionnalProblemError) {
    return <GenericProblemMessage {...error.params} />
  }

  if (error instanceof NotFoundError || error instanceof SearchParamError) {
    return <NotFound />
  }

  if (error instanceof InvalidLinkError) {
    return <InvalidLinkMessage />
  }

  logger.error(error)
  return <CrashPage />
})
